<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div
      class="row"
      v-if="userData.permissions['view_student_attendance'] == 1"
    >
      <div class="row">
        <div class="col-12">
          <div class="card shadow-lg">
            <div class="card-body">
              <!-- FILTER SECTION -->
              <div class="my-2" v-if="students.length <= 0">
                <div class="row align-items-end">
                  <b-form-group class="col-md-3 px-1" label="Class">
                    <v-select
                      :options="schoolclasses"
                      @input="FilterStreams"
                      placeholder="Select Class"
                      v-model="schoolclass"
                      label="name"
                    ></v-select>
                  </b-form-group>
                  <b-form-group class="col-md-3 px-1" label="Stream">
                    <v-select
                      :options="streams"
                      placeholder="Select Stream"
                      v-model="stream"
                      label="name"
                    ></v-select>
                  </b-form-group>
                  <b-form-group class="col-md-2 px-1" label="Date">
                    <b-form-input
                      v-model="attendanceDate"
                      type="date"
                      :max="currentdate"
                    ></b-form-input>
                  </b-form-group>
                  <div class="col-md-2 mt-md-4 mt-2 p-lg-0">
                    <b-button variant="primary" @click="LoadStudents()">
                      <i class="mdi mdi-account-search"></i> Load Students
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- ATTENDANCE TABLE SECTION -->
              <div v-else>
                <!-- Top bar: title + reset button -->
                <div
                  class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
                >
                  <span class="h5 text-center mb-0">
                    Attendance &mdash;
                    <span class="text-primary">{{ schoolclass.name }}</span>
                    <span v-if="stream"> / {{ stream.name }}</span>
                    &nbsp;|&nbsp; {{ attendanceDate }}
                  </span>
                  <div class="d-flex gap-2">
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      @click="MarkAll(true)"
                    >
                      <i class="mdi mdi-check-all"></i> Mark All Present
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="MarkAll(false)"
                    >
                      <i class="mdi mdi-close-box-multiple"></i> Mark All Absent
                    </b-button>
                    <b-button
                      variant="outline-warning"
                      size="sm"
                      @click="ResetPage()"
                    >
                      <i class="mdi mdi-refresh"></i> Change Class
                    </b-button>
                  </div>
                </div>

                <!-- Summary badges -->
                <div class="mb-3 d-flex gap-3 flex-wrap">
                  <span class="badge bg-success fs-6 px-3 py-2">
                    <i class="mdi mdi-account-check"></i>
                    Present: {{ presentCount }}
                  </span>
                  <span class="badge bg-danger fs-6 px-3 py-2">
                    <i class="mdi mdi-account-remove"></i>
                    Absent: {{ absentCount }}
                  </span>
                  <span class="badge bg-warning fs-6 px-3 py-2">
                    <i class="mdi mdi-comment-alert-outline"></i>
                    Absent with Apology: {{ absentWithReasonCount }}
                  </span>
                  <span class="badge bg-secondary fs-6 px-3 py-2">
                    <i class="mdi mdi-account-group"></i>
                    Total: {{ students.length }}
                  </span>
                </div>
                <div class="mb-3">
                  <small class="text-muted">
                    Use the Attendance dropdown for each learner. Select “Absent
                    + Apology” to provide a reason.
                  </small>
                </div>

                <!-- Per-page + Search controls -->
                <div class="row mb-2">
                  <div class="col-sm-12 col-md-6">
                    <div class="dataTables_length">
                      <label class="d-inline-flex align-items-center">
                        Show
                        <b-form-select
                          class="form-select form-select-sm mx-1"
                          v-model="perPage"
                          size="sm"
                          :options="pageOptions"
                        ></b-form-select>
                        entries
                      </label>
                    </div>
                  </div>
                  <div class="col-sm-12 col-md-6">
                    <div class="dataTables_filter text-md-end">
                      <label class="d-inline-flex align-items-center">
                        Search:
                        <b-form-input
                          v-model="filter"
                          type="search"
                          placeholder="Search student..."
                          class="form-control form-control-sm ms-2"
                        ></b-form-input>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Attendance Table -->
                <div class="table-responsive">
                  <table class="table table-hover table-bordered align-middle">
                    <thead class="table-dark">
                      <tr>
                        <th style="width:50px">#</th>
                        <th style="width:70px">Photo</th>
                        <th>Student ID</th>
                        <th>Full Name</th>
                        <th>Gender</th>
                        <th class="text-center" style="width:220px">
                          Attendance
                        </th>
                        <th style="width:260px">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(student, index) in paginatedStudents"
                        :key="student.id"
                        :class="{
                          'table-success': student.rollcall_status === 1,
                          'table-danger': student.rollcall_status === 0,
                          'table-warning': student.rollcall_status === 2,
                        }"
                        style="transition: background 0.2s;"
                      >
                        <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                        <td>
                          <img
                            :src="getFiles(student.student_id.student_photo)"
                            height="45"
                            class="rounded-circle"
                            style="object-fit:cover; width:45px;"
                          />
                        </td>
                        <td>
                          <b class="text-danger">{{
                            student.student_id.student_id
                          }}</b>
                        </td>
                        <td> 
                          {{ student.student_id.first_name }}
                          {{ student.student_id.last_name }}
                        </td>
                        <td>{{ student.student_id.gender }}</td>
                        <td>
                          <b-form-select
                            v-model="student.rollcall_status"
                            :options="statusOptions"
                            size="sm"
                            @change="onStatusChange(student)"
                          ></b-form-select>
                        </td>
                        <td>
                          <b-form-input
                            v-model="student.absence_reason"
                            :disabled="student.rollcall_status !== 2"
                            placeholder="Enter apology or absence reason"
                            size="sm"
                            @click.stop
                          ></b-form-input>
                        </td>
                      </tr>
                      <tr v-if="paginatedStudents.length === 0">
                        <td colspan="7" class="text-center text-muted py-4">
                          No students found.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="row">
                  <div class="col">
                    <div
                      class="dataTables_paginate paging_simple_numbers float-end"
                    >
                      <ul class="pagination pagination-rounded mb-0">
                        <b-pagination
                          v-model="currentPage"
                          :total-rows="filteredStudents.length"
                          :per-page="perPage"
                        ></b-pagination>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="row mt-4">
                  <div class="col-12 text-end">
                    <b-button
                      variant="success"
                      size="lg"
                      @click="SubmitAttendance()"
                      :disabled="isSubmitting"
                    >
                      <b-spinner small v-if="isSubmitting"></b-spinner>
                      <i class="mdi mdi-content-save-check" v-else></i>
                      &nbsp; Submit Attendance
                    </b-button>
                  </div>
                </div>
              </div>
              <!-- END attendance table section -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No permission fallback -->
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div>
            <center>
              <span class="text-primary h4"
                >You have no permission to view this page.</span
              >
            </center>
            <img
              src="@/assets/images/profile-img.png"
              alt
              class="img-fluid"
            /><br /><br />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import {
  userMethods,
  userComputed,
  schoolclassMethods,
  schoolclassComputed,
  schooltermMethods,
  schooltermComputed,
} from "@/state/helpers";

export default {
  page: {
    title: "Attendance",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "Attendance",
      items: [
        { text: "Dashboard", to: "/" },
        { text: "Attendance", active: true },
      ],

      currentdate: new Date().toLocaleDateString("fr-CA"),
      attendance_id: "",
      schoolclass: "",
      streams: [],
      stream: "",
      attendanceDate: new Date().toISOString().split("T")[0],
      // Students
      students: [],
      // Table controls
      perPage: 50,
      pageOptions: [25, 50, 100, 200],
      currentPage: 1,
      filter: "",
      // UI state
      isSubmitting: false,
      statusOptions: [
        { value: 1, text: "Present" },
        { value: 0, text: "Absent" },
        { value: 2, text: "Absent + Apology" },
      ],
    };
  },
  computed: {
    ...schoolclassComputed,
    ...schooltermComputed,
    ...userComputed,

    presentCount() {
      return this.students.filter((s) => s.rollcall_status === 1).length;
    },

    absentCount() {
      return this.students.filter((s) => s.rollcall_status === 0).length;
    },

    absentWithReasonCount() {
      return this.students.filter((s) => s.rollcall_status === 2).length;
    },

    // Filter students by search input
    filteredStudents() {
      if (!this.filter) return this.students;
      const q = this.filter.toLowerCase();
      return this.students.filter((s) => {
        const name = `${s.student_id.first_name} ${s.student_id.last_name}`.toLowerCase();
        const studentId = String(s.student_id.student_id).toLowerCase();
        return name.includes(q) || studentId.includes(q);
      });
    },

    // Paginate the filtered list
    paginatedStudents() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredStudents.slice(start, start + this.perPage);
    },
  },
  mounted() {
    //this.FetchClasses();
    this.fetchSchoolClasses();
  },
  methods: {
    ...schoolclassMethods,
    ...schooltermMethods,
    ...userMethods,

    // async FetchClasses() {
    //   const url = `/findschoolclass/${this.userData.user_id}`;
    //   await axios.get(url).then((response) => {
    //     this.schoolclasses = response.data.classes;
    //   });
    // },

    async FilterStreams() {
      this.stream = "";
      this.streams = [];
      if (!this.schoolclass) return;
      const url = `/findstream/${this.schoolclass.id}`;
      await axios.get(url).then((response) => {
        this.streams = response.data.streams;
      });
    },

    async LoadStudents() {
      if (!this.schoolclass) {
        this.$bvToast.toast("Please select a class.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const formData = new FormData();
      formData.append("date", this.attendanceDate);
      formData.append("schoolclass", this.schoolclass.id);
      formData.append("stream", this.stream ? this.stream.id : "");

      await axios.post("/loadattendancestudents", formData).then((response) => {
        // If attendance is already taken
        if (response.data.already_taken) {
          this.$bvModal.msgBoxOk(response.data.message, {
            title: "Attendance Already Taken",
            titleTag: "h5",
            okVariant: "warning",
            okTitle: "OK, Got it",
            headerClass: "bg-warning text-dark",
            bodyClass: "text-dark fs-6",
            footerClass: "border-0",
            centered: true,
          });
          return;
        }
        //If no students are found
        if (response.data.no_students) {
          this.$bvModal.msgBoxOk(response.data.message, {
            title: "No Students Found",
            titleTag: "h5",
            okVariant: "info",
            okTitle: "OK",
            headerClass: "bg-info text-white",
            bodyClass: "text-dark fs-6",
            footerClass: "border-0",
            centered: true,
          });
          return;
        }

        // Store the attendance_id for use when saving
        this.attendance_id = response.data.attendance_id;

        this.students = response.data.students.map((s) => ({
          ...s,
          rollcall_status: s.attendance ?? 0,
          absence_reason: s.absence_reason || "",
        }));
        this.currentPage = 1;
      });
    },

    setAttendanceStatus(student, status) {
      student.rollcall_status = status;
      if (status !== 2) {
        student.absence_reason = "";
      }
    },

    onStatusChange(student) {
      if (student.rollcall_status !== 2) {
        student.absence_reason = "";
      }
    },

    MarkAll(present) {
      this.students = this.students.map((s) => ({
        ...s,
        rollcall_status: present ? 1 : 0,
        absence_reason: "",
      }));
    },

    ResetPage() {
      this.students = [];
      this.streams = [];
      this.stream = "";
      this.schoolclass = "";
      this.currentPage = 1;
      this.filter = "";
    },

    getFiles(name) {
      const url = axios.defaults.baseURL;
      return url.slice(0, -3) + "students/" + name;
    },

    async SubmitAttendance() {
      this.isSubmitting = true;

      const payload = this.students.map((s) => ({
        student_id: s.student_id.student_id,
        rollcall_status: s.rollcall_status,
        absence_reason: s.absence_reason || "",
      }));

      const formData = new FormData();
      formData.append("attendance_id", this.attendance_id);
      formData.append("attendance", JSON.stringify(payload));
      formData.append("user", this.userData.user_id);

      await axios
        .post("/saveattendance", formData)
        .then((response) => {
          if (response.data.code === 200 || response.status === 200) {
            this.$bvToast.toast("Attendance saved successfully!", {
              title: "Success",
              variant: "success",
              solid: true,
            });
            // Clear the page after successful save
            this.ResetPage();
            this.attendance_id = "";
          }
        })
        .catch(() => {
          this.$bvToast.toast("Failed to save attendance. Try again.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
  },
};
</script>

<style scoped>
/* Attendance checkbox styling */
.attendance-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 2px solid #ced4da;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, border-color 0.18s, transform 0.1s;
  background: #fff;
}

.attendance-box.checked {
  background: #28a745;
  border-color: #28a745;
  transform: scale(1.08);
}

.attendance-box.unchecked:hover {
  border-color: #28a745;
  background: #e9fbe9;
}

/* Highlight present rows */
.table-success td {
  background-color: #d4edda !important;
}

/* Smooth row transitions */
tbody tr {
  transition: background-color 0.2s;
}
</style>
